import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import {
  deleteThemeById,
  GameplayThemeData,
  getAllThemes,
  updateThemesOrder,
} from '../../../../services/api/themesApi';
import { Button } from '../../../../shared/Components';
import { Box, FlexContainer, Text } from '../../../../shared/Typhography';

import ThemeGridItemPreview from './components/ThemeGridItemPreview';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #888;
  margin-top: 80px;
  font-size: 16px;
`;

const Header = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
`;

const ReorderFooter = styled(FlexContainer)`
  margin-top: 32px;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #eee;
  background: #fafafa;
  border-radius: 8px;
`;

const ReorderHint = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;
`;

export const ThemesScreen: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showModal } = useGlobalModalContext();

  const [isReorderMode, setIsReorderMode] = useState(false);
  const [orderedThemes, setOrderedThemes] = useState<GameplayThemeData[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const { data: themes = [], isLoading } = useQuery(['themes'], getAllThemes, {
    refetchOnWindowFocus: false,
  });

  const handleEdit = useCallback(
    (id: string) => {
      history.push(`/manage-theme/${id}`);
    },
    [history],
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      showModal(MODAL_TYPES.DIALOG_MODAL, {
        title: 'Delete Theme?',
        text: `Are you sure you want to delete the theme "${name}"? This cannot be undone.`,
        cancelButtonText: 'Cancel',
        submitButtonText: 'Delete',
        onSubmit: async () => {
          try {
            await deleteThemeById(id);
            await queryClient.invalidateQueries(['themes']);
            toast.success('Theme deleted.');
          } catch {
            toast.error('Failed to delete theme.');
          }
        },
      });
    },
    [showModal, queryClient],
  );

  const enterReorderMode = useCallback(() => {
    setOrderedThemes([...themes]);
    setIsReorderMode(true);
  }, [themes]);

  const cancelReorderMode = useCallback(() => {
    setIsReorderMode(false);
    setOrderedThemes([]);
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  const swapThemes = useCallback((fromIndex: number, toIndex: number) => {
    setOrderedThemes((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const saveOrder = useCallback(async () => {
    setIsSavingOrder(true);
    try {
      const updates = orderedThemes.map((theme, index) => ({ id: theme.id, order: index }));
      await updateThemesOrder(updates);
      await queryClient.invalidateQueries(['themes']);
      toast.success('Theme order saved!');
      setIsReorderMode(false);
      setOrderedThemes([]);
    } catch {
      toast.error('Failed to save order. Please try again.');
    } finally {
      setIsSavingOrder(false);
    }
  }, [orderedThemes, queryClient]);

  // Drag-and-drop handlers
  const handleDragStart = useCallback(
    (index: number) => (e: React.DragEvent) => {
      dragIndexRef.current = index;
      setDragIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleDragOver = useCallback(
    (index: number) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverIndex(index);
    },
    [],
  );

  const handleDragLeave = useCallback(
    () => () => {
      setDragOverIndex(null);
    },
    [],
  );

  const handleDrop = useCallback(
    (toIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      const fromIndex = dragIndexRef.current;
      if (fromIndex != null && fromIndex !== toIndex) {
        swapThemes(fromIndex, toIndex);
      }
      setDragIndex(null);
      setDragOverIndex(null);
      dragIndexRef.current = null;
    },
    [swapThemes],
  );

  const handleDragEnd = useCallback(
    () => () => {
      setDragIndex(null);
      setDragOverIndex(null);
      dragIndexRef.current = null;
    },
    [],
  );

  const displayThemes = isReorderMode ? orderedThemes : themes;

  return (
    <Box margin="120px auto 40px auto" style={{ maxWidth: 1100, padding: '0 24px' }}>
      <Header>
        <Text size={2} weight="bold">
          Themes
        </Text>
        <FlexContainer gap="12px" alignItems="center">
          {!isReorderMode && (
            <>
              <Button
                variant="secondary"
                height={40}
                style={{ minWidth: 150 }}
                onClick={enterReorderMode}
                disabled={themes.length < 2}
              >
                Change Order
              </Button>
              <Button
                variant="primary"
                height={40}
                style={{ minWidth: 160 }}
                onClick={() => history.push('/manage-theme')}
              >
                + Create Theme
              </Button>
            </>
          )}
          {isReorderMode && (
            <Text size={1} style={{ color: '#555' }}>
              Reorder mode active
            </Text>
          )}
        </FlexContainer>
      </Header>

      {isLoading ? (
        <EmptyState>Loading themes…</EmptyState>
      ) : displayThemes.length === 0 ? (
        <EmptyState>No themes yet. Create your first theme.</EmptyState>
      ) : (
        <Grid>
          {displayThemes.map((theme, index) => (
            <ThemeGridItemPreview
              key={theme.id}
              name={theme.name}
              previewUrl={theme.previewUrl}
              gameplayBackgroundColor={theme.gameplayBackgroundColor}
              gameplayBackgroundImageUrl={theme.gameplayBackgroundImageUrl}
              boardBackgroundColor={theme.boardBackgroundColor}
              boardBackgroundImageUrl={theme.boardBackgroundImageUrl}
              buttonsBackgroundColor={theme.buttonsBackgroundColor}
              buttonsIconsColor={theme.buttonsIconsColor}
              onEdit={() => handleEdit(theme.id)}
              onDelete={() => handleDelete(theme.id, theme.name)}
              isReorderMode={isReorderMode}
              positionIndex={index}
              canMoveUp={index > 0}
              canMoveDown={index < displayThemes.length - 1}
              onMoveUp={() => swapThemes(index, index - 1)}
              onMoveDown={() => swapThemes(index, index + 1)}
              isDragging={dragIndex === index}
              isDragOver={dragOverIndex === index && dragIndex !== index}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDragLeave={handleDragLeave()}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd()}
            />
          ))}
        </Grid>
      )}

      {isReorderMode && (
        <ReorderFooter>
          <ReorderHint>
            Drag tiles or use ↑ Up / ↓ Down buttons to reorder. Click Save Order to persist.
          </ReorderHint>
          <Button
            variant="danger"
            height={40}
            style={{ minWidth: 100 }}
            onClick={cancelReorderMode}
            disabled={isSavingOrder}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            height={40}
            style={{ minWidth: 140 }}
            onClick={saveOrder}
            disabled={isSavingOrder}
          >
            {isSavingOrder ? 'Saving…' : 'Save Order'}
          </Button>
        </ReorderFooter>
      )}
    </Box>
  );
};
